import React, { useState } from 'react';
import { Badge, Box, Button, HStack, Select, Text, VStack, useToast } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { ROLE_LABELS, apiErrorText } from '../../utils/roles';

/**
 * "Équipe" block of the event settings (#70, admin only): who may work on this event.
 * Admins see every event without an assignment, so they are not listed here.
 */
const EventTeamSection: React.FC<{ eventId: number }> = ({ eventId }) => {
  const toast = useToast();
  const qc = useQueryClient();
  const [selected, setSelected] = useState('');
  const teamKey = ['event-team', eventId];

  const teamQ = useQuery({ queryKey: teamKey, queryFn: () => apiService.getEventTeam(eventId) });
  const usersQ = useQuery({ queryKey: ['users'], queryFn: () => apiService.listUsers() });

  const onError = (err: any) => toast({ title: apiErrorText(err), status: 'error', duration: 6000 });
  const refresh = () => qc.invalidateQueries({ queryKey: teamKey });

  const add = useMutation({
    mutationFn: (userId: number) => apiService.addEventTeamMember(eventId, userId),
    onSuccess: () => { setSelected(''); refresh(); },
    onError,
  });
  const remove = useMutation({
    mutationFn: (userId: number) => apiService.removeEventTeamMember(eventId, userId),
    onSuccess: refresh,
    onError,
  });

  const team = teamQ.data ?? [];
  const inTeam = new Set(team.map((m) => m.id));
  const candidates = (usersQ.data ?? []).filter((u) => u.is_active && u.role !== 'admin' && !inTeam.has(u.id));

  return (
    <VStack align="stretch" spacing={3} data-testid="event-team">
      {teamQ.isLoading && <Text fontSize="sm" color="gray.500">Chargement…</Text>}
      {!teamQ.isLoading && team.length === 0 && (
        <Text fontSize="sm" color="gray.500" data-testid="event-team-empty">
          Personne n'est assigné à cet événement (les administrateurs y ont toujours accès).
        </Text>
      )}
      {team.map((m) => (
        <HStack key={m.id} justify="space-between" data-testid={`event-team-member-${m.id}`}>
          <Box>
            <Text fontWeight="semibold" fontSize="sm">{m.first_name} {m.last_name}</Text>
            <Text fontSize="xs" color="gray.500">{m.email}</Text>
          </Box>
          <HStack>
            <Badge>{ROLE_LABELS[m.role]}</Badge>
            <Button size="xs" variant="outline" onClick={() => remove.mutate(m.id)} isDisabled={remove.isPending}>
              Retirer
            </Button>
          </HStack>
        </HStack>
      ))}
      <HStack>
        <Select
          size="sm"
          placeholder="Ajouter une personne…"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          data-testid="event-team-select"
        >
          {candidates.map((u) => (
            <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({ROLE_LABELS[u.role]})</option>
          ))}
        </Select>
        <Button
          size="sm"
          colorScheme="blue"
          isDisabled={!selected || add.isPending}
          onClick={() => add.mutate(Number(selected))}
          data-testid="event-team-add"
        >
          Ajouter
        </Button>
      </HStack>
    </VStack>
  );
};

export default EventTeamSection;
